import { ReactNode, useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import SpellIcon from './SpellIcon'
import TeamPlanActorRows from './TeamPlanActorRows'
import { useTeamPlanTimeline } from './TeamPlanTimelineContext'
import TimelineContainer from './TimelineContainer'
import TimelineAbilitySection from './TimelineAbilitySection'
import { Separator } from '@/components/ui/separator'
import BlizzardIcon from 'app/frontend/base/components/BlizzardIcon'
import { NOTE_ABILITY_ID, NOTE_ICON_NAME } from '../config/generalAbilities'
import { abilityTypes, AbilityType } from 'app/frontend/types/custom/Abilities'
import AddActorBar from './AddActorBar'
import ActorTimelineContainer, {
  ActorTimelineRow,
  sortActorTimelineRows,
} from './ActorTimelineContainer'
import ActorAbilityContainer from './ActorAbilityContainer'
import ActorHeader from './ActorHeader'
import TeamPlanAssignments from './TeamPlanAssignments'
import mapGeneralCastsWithOverlap from './generalCastOverlap'
import { getViewerColorClass } from './viewerPresence'
import { getRaidPlanAbilities } from 'app/frontend/base/services/raidPlanData'
import { getEncounterAssignmentAbilities } from './assignmentTimeline'
import { useStickyTop } from 'app/frontend/base/hooks/useStickyTop'
import { Button } from '@/components/ui/button'
import Card from 'app/frontend/base/components/Card'
import RaidPlan from 'app/frontend/types/custom/RaidPlan'
import ImportFromWarcraftLogsDialog from './ImportFromWarcraftLogsDialog'
import CopyFromPlanPopover from './CopyFromPlanPopover'
import { imageUrl } from 'app/frontend/base/helpers'

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value))

const cloneTimeline = <T,>(value: T): T =>
  JSON.parse(JSON.stringify(value)) as T

type TeamPlanTimelineProps = {
  inFocusedDialog?: boolean
  actorRowBehaviorByUuid?: Record<
    string,
    {
      showPreferredCharacter?: boolean
      allowRemove?: boolean
      allowSpecializationEdit?: boolean
      allowCastEdit?: boolean
    }
  >
  beforeActorContentByUuid?: Record<string, ReactNode>
  extraActorContent?: ReactNode
}

const TeamPlanTimeline = ({
  inFocusedDialog = false,
  actorRowBehaviorByUuid,
  beforeActorContentByUuid,
  extraActorContent,
}: TeamPlanTimelineProps) => {
  const { t } = useTranslation()
  const {
    canEdit,
    raidPlan,
    viewers,
    timeline,
    computed,
    selectedType,
    setSelectedType,
    zoom,
    timelineRootRef,
    cursors,
    cursorContext,
    sendCursor,
    updateTimeline,
  } = useTeamPlanTimeline()
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false)
  const {
    encounter,
    maxTime,
    phaseDividers,
    phaseBoundaryHandles,
    encounterAbilityRows,
    generalAbilities,
  } = computed
  const visibleRange = useMemo(() => {
    const start = clamp(zoom?.start ?? 0, 0, maxTime)
    const end = clamp(zoom?.end ?? maxTime, start + 1, maxTime)
    return { start, end }
  }, [maxTime, zoom?.end, zoom?.start])
  const [hoverTime, setHoverTime] = useState<number | null>(null)
  const [actorCastPopoverOpen, setActorCastPopoverOpen] = useState(false)
  const [generalCastPopoverOpen, setGeneralCastPopoverOpen] = useState(false)
  const shouldHideHoverIndicators =
    actorCastPopoverOpen || generalCastPopoverOpen
  const visibleHoverTime = shouldHideHoverIndicators ? null : hoverTime
  const generalRows = useMemo<ActorTimelineRow[]>(
    () =>
      generalAbilities.map((ability) => {
        const rowSpellId = ability.id === NOTE_ABILITY_ID ? null : ability.id
        return {
          key: `general-${ability.id}`,
          spellId: rowSpellId,
          casts: mapGeneralCastsWithOverlap(
            (timeline.general?.casts ?? []).filter(
              (cast) => cast.spell_id === rowSpellId,
            ),
            ability.cooldown ?? 0,
            'var(--color-primary)',
          ),
        }
      }),
    [generalAbilities, timeline.general?.casts],
  )
  const generalAbilityById = useMemo(
    () =>
      generalAbilities.reduce<Record<number, typeof generalAbilities[number]>>(
        (acc, ability) => {
          acc[ability.id] = ability
          return acc
        },
        {},
      ),
    [generalAbilities],
  )
  const sortedGeneralRows = useMemo(
    () =>
      sortActorTimelineRows(
        generalRows,
        (spellId) => {
          if (spellId === null) {
            return (
              generalAbilities.find((ability) => ability.id === NOTE_ABILITY_ID)
                ?.name ?? 'Note'
            )
          }
          return generalAbilityById[spellId]?.name ?? `Spell ${spellId}`
        },
        (spellId) =>
          spellId === null ? 0 : generalAbilityById[spellId]?.cooldown ?? 0,
      ),
    [generalAbilities, generalAbilityById, generalRows],
  )

  const lastCursorAtRef = useRef(0)
  const handleTimelineMouseMove = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const now = Date.now()
      if (now - lastCursorAtRef.current < 100) return
      lastCursorAtRef.current = now
      const rect = event.currentTarget.getBoundingClientRect()
      if (rect.width <= 0 || rect.height <= 0) return
      const x = (event.clientX - rect.left) / rect.width
      const y = (event.clientY - rect.top) / rect.height
      sendCursor(
        Number(clamp(x, 0, 1).toFixed(4)),
        Number(clamp(y, 0, 1).toFixed(4)),
        cursorContext,
      )
    },
    [cursorContext, sendCursor],
  )
  const handleTimelineMouseLeave = useCallback(() => {
    sendCursor(null, null, cursorContext)
    setHoverTime(null)
  }, [cursorContext, sendCursor])

  const updatePhaseBoundary = useCallback(
    (boundaryIndex: number, nextAt: number, pushHistory: boolean) => {
      const handlePosition = phaseBoundaryHandles.findIndex(
        (entry) => entry.index === boundaryIndex,
      )
      const handle = phaseBoundaryHandles[handlePosition]
      if (!handle) return
      const clampedTarget = Number(
        clamp(nextAt, handle.min, handle.max).toFixed(2),
      )
      const currentBoundaryAt = Number(handle.at.toFixed(2))
      if (clampedTarget === currentBoundaryAt) return

      updateTimeline((next) => {
        const defaults = phaseBoundaryHandles.map((entry) => entry.at)
        const nextPhaseBoundaries =
          Array.isArray(next.phase_boundaries) &&
          next.phase_boundaries.length >= defaults.length
            ? [...next.phase_boundaries]
            : defaults
        const previousBoundaryAt = nextPhaseBoundaries[handlePosition]
        if (typeof previousBoundaryAt !== 'number') return
        const delta = clampedTarget - previousBoundaryAt
        if (delta === 0) return
        nextPhaseBoundaries.forEach((value, index) => {
          if (index < handlePosition) return
          nextPhaseBoundaries[index] = Number((value + delta).toFixed(2))
        })
        next.phase_boundaries = nextPhaseBoundaries
      }, pushHistory)
    },
    [phaseBoundaryHandles, updateTimeline],
  )

  const handleTimelineHoverMove = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const rect = event.currentTarget.getBoundingClientRect()
      const localX = event.clientX - rect.left
      if (rect.width <= 0) {
        setHoverTime(null)
        return
      }
      const ratio = clamp(localX / rect.width, 0, 1)
      const nextTime =
        visibleRange.start + ratio * (visibleRange.end - visibleRange.start)
      setHoverTime(Number(clamp(nextTime, 0, maxTime).toFixed(2)))
    },
    [maxTime, visibleRange.end, visibleRange.start],
  )
  const handleTimelineHoverMoveFromCoordinates = useCallback(
    (clientX: number, timelineLeft: number, timelineWidth: number) => {
      if (timelineWidth <= 0) {
        setHoverTime(null)
        return
      }
      const ratio = clamp((clientX - timelineLeft) / timelineWidth, 0, 1)
      const nextTime =
        visibleRange.start + ratio * (visibleRange.end - visibleRange.start)
      setHoverTime(Number(clamp(nextTime, 0, maxTime).toFixed(2)))
    },
    [maxTime, visibleRange.end, visibleRange.start],
  )

  const handleTimelineHoverLeave = useCallback(() => {
    setHoverTime(null)
  }, [])
  const handleTimelineHoverTimeChange = useCallback(
    (nextHoverTime: number) => {
      setHoverTime(
        Number(
          clamp(nextHoverTime, visibleRange.start, visibleRange.end).toFixed(2),
        ),
      )
    },
    [visibleRange.end, visibleRange.start],
  )
  const stickyTop = useStickyTop()
  const viewerIndexByKey = useMemo(
    () =>
      viewers.reduce<Record<string, number>>((acc, viewer, index) => {
        acc[viewer.key] = index
        return acc
      }, {}),
    [viewers],
  )
  const visibleCursors = useMemo(
    () =>
      cursorContext.startsWith('tab:')
        ? cursors.filter((cursor) => cursor.context === cursorContext)
        : [],
    [cursorContext, cursors],
  )
  const tabCastCounts = useMemo<Record<AbilityType, number>>(() => {
    const counts = abilityTypes.reduce<Record<AbilityType, number>>(
      (acc, type) => {
        acc[type] = 0
        return acc
      },
      {} as Record<AbilityType, number>,
    )
    const abilities = getRaidPlanAbilities()
    const classSpells = abilities.classSpells
    const specSpells = abilities.specSpells
    const generalAbilityIds = new Set(
      generalAbilities.map((ability) => ability.id),
    )
    timeline.actors.forEach((actor) => {
      const classSpellsById = (
        classSpells[actor.class_id.toString()] ?? []
      ).reduce<Record<number, AbilityType>>((acc, spell) => {
        acc[spell.id] = spell.type
        return acc
      }, {})
      const specSpellsById = actor.spec_id
        ? (specSpells[actor.spec_id.toString()] ?? []).reduce<
            Record<number, AbilityType>
          >((acc, spell) => {
            acc[spell.id] = spell.type
            return acc
          }, {})
        : {}
      actor.casts.forEach((cast) => {
        if (cast.spell_id === null) {
          counts.general += 1
          return
        }
        if (generalAbilityIds.has(cast.spell_id)) {
          counts.general += 1
          return
        }
        const castType =
          specSpellsById[cast.spell_id] ?? classSpellsById[cast.spell_id]
        if (!castType) return
        counts[castType] += 1
      })
    })
    const globalGeneralCastCount = timeline.general?.casts.length ?? 0
    counts.key_cooldown += globalGeneralCastCount
    counts.assignments = getEncounterAssignmentAbilities(timeline).length
    return counts
  }, [
    generalAbilities,
    timeline.actors,
    timeline.encounter_assignments,
    timeline.general?.casts.length,
  ])
  const hideAssignmentsTab = inFocusedDialog
  const hideAddActorBar = inFocusedDialog
  const hideGeneralSection = inFocusedDialog
  const showTimelineOnboarding =
    canEdit &&
    !inFocusedDialog &&
    selectedType !== 'assignments' &&
    timeline.actors.length === 0
  const showDefaultAddActorBar =
    canEdit && !hideAddActorBar && !showTimelineOnboarding
  const visibleAbilityTypes = useMemo(
    () =>
      abilityTypes.filter(
        (type) => !(hideAssignmentsTab && type === 'assignments'),
      ),
    [hideAssignmentsTab],
  )
  const showAssignmentsSection =
    !hideAssignmentsTab && selectedType === 'assignments'
  const applyTimelineFromPlan = useCallback(
    (plan: RaidPlan) => {
      const sourceTimeline = cloneTimeline(plan.timeline)
      updateTimeline((next) => {
        Object.keys(next).forEach((key) => {
          delete (next as Record<string, unknown>)[key]
        })
        Object.assign(next, sourceTimeline)
      })
    },
    [updateTimeline],
  )

  return (
    <div
      className='relative'
      ref={timelineRootRef}
      onMouseMove={handleTimelineMouseMove}
      onMouseLeave={handleTimelineMouseLeave}
    >
      <div className='w-full relative'>
        <div
          className={
            inFocusedDialog
              ? ''
              : cn(
                  'z-30 bg-card pb-1',
                  selectedType === 'assignments' ? '' : 'sticky',
                  selectedType === 'assignments' ? '' : stickyTop,
                )
          }
        >
          <TimelineAbilitySection
            title={
              <div className='flex items-center gap-1.5'>
                {encounter?.thumbnailUrl ? (
                  <img
                    className='w-4 h-4 rounded-md shrink-0'
                    src={encounter.thumbnailUrl}
                  />
                ) : (
                  <div className='w-4 h-4 rounded-md border bg-muted' />
                )}
                <span>{encounter?.name}</span>
              </div>
            }
            labels={encounterAbilityRows.map((row) => (
              <div
                key={`${row.spellId}-${row.name}`}
                className='flex items-center gap-2'
              >
                <a
                  href={`https://wowhead.com/spell=${row.spellId}`}
                  target='_blank'
                  rel='noreferrer'
                  className='w-40 max-w-40 shrink-0 h-5 text-xs text-muted-foreground truncate flex items-center gap-2'
                  style={{ color: row.color }}
                >
                  <span className='truncate grow text-right'>{row.name}</span>
                  <SpellIcon
                    spellId={row.spellId}
                    className='w-5 h-5 rounded border border-border'
                  />
                </a>
              </div>
            ))}
            timeline={
              <TimelineContainer
                maxTime={maxTime}
                phaseDividers={phaseDividers}
                phaseBoundaryHandles={phaseBoundaryHandles}
                rows={encounterAbilityRows}
                hoverTime={visibleHoverTime}
                onPhaseBoundaryDrag={
                  canEdit
                    ? (boundaryIndex, nextAt) =>
                        updatePhaseBoundary(boundaryIndex, nextAt, false)
                    : undefined
                }
                onPhaseBoundaryDragEnd={
                  canEdit
                    ? (boundaryIndex, nextAt) =>
                        updatePhaseBoundary(boundaryIndex, nextAt, true)
                    : undefined
                }
                className={inFocusedDialog ? 'text-card' : 'text-card-nested'}
              />
            }
            onTimelineMouseMove={handleTimelineHoverMove}
            onTimelineMouseLeave={handleTimelineHoverLeave}
          />

          <Separator className='mt-1' />
        </div>

        <div className='py-2 flex items-center justify-between gap-3 flex-wrap'>
          <Tabs
            value={selectedType}
            onValueChange={(value) => setSelectedType(value as AbilityType)}
          >
            <TabsList className='flex-wrap'>
              {visibleAbilityTypes.map((type) => (
                <TabsTrigger
                  key={type}
                  value={type}
                  className='h-auto py-1 group'
                >
                  <span className='flex flex-col leading-tight text-center gap-0.5'>
                    <span>
                      {t(`dashboard.team.plans.ability-types.${type}`)}
                    </span>
                    <span className='text-[11px] text-muted-foreground/70 font-normal group-data-[state=active]:text-primary-foreground'>
                      {tabCastCounts[type] ?? 0}{' '}
                      {type === 'assignments'
                        ? (tabCastCounts[type] ?? 0) === 1
                          ? 'ability'
                          : 'abilities'
                        : (tabCastCounts[type] ?? 0) === 1
                        ? 'cast'
                        : 'casts'}
                    </span>
                  </span>
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          {showDefaultAddActorBar ? <AddActorBar /> : null}
        </div>

        {showTimelineOnboarding && (
          <div className='p-4 mb-4 flex flex-col items-center gap-2'>
            <h4>Get started</h4>
            <div className='flex items-center gap-2 flex-wrap'>
              <Button
                variant='outline'
                className='h-32 w-52 flex flex-col gap-3 items-center justify-center'
                onClick={() => setIsImportDialogOpen(true)}
              >
                <img src={imageUrl('wcl-logo.png')} className='w-8 h-8' />
                {t('dashboard.team.plans.onboarding.import-from-warcraft-logs')}
              </Button>
              <CopyFromPlanPopover
                currentPlan={raidPlan}
                buttonLabel={t(
                  'dashboard.team.plans.onboarding.copy-from-another-plan',
                )}
                emptyLabel={t('dashboard.team.plans.onboarding.copy-empty')}
                onSelectPlan={applyTimelineFromPlan}
              />
            </div>
            <div className='flex items-center gap-3 my-3'>
              <div className='h-px bg-border grow' />
              <span className=''>
                {t('dashboard.team.plans.onboarding.or-add-characters')}
              </span>
              <div className='h-px bg-border grow' />
            </div>
            <Card>
              <AddActorBar />
            </Card>
          </div>
        )}

        {!showTimelineOnboarding &&
          selectedType !== 'assignments' &&
          !timeline.actors.length && (
            <div className='py-10 text-center text-sm text-muted-foreground'>
              {t('dashboard.team.plans.empty-actors')}
            </div>
          )}

        {showAssignmentsSection ? (
          <TeamPlanAssignments />
        ) : (
          <TeamPlanActorRows
            hoverTime={visibleHoverTime}
            onTimelineMouseMove={handleTimelineHoverMove}
            onTimelineMouseLeave={handleTimelineHoverLeave}
            onTimelineHoverTimeChange={handleTimelineHoverTimeChange}
            onActorRowHoverMove={handleTimelineHoverMoveFromCoordinates}
            onCastNotePopoverOpenChange={setActorCastPopoverOpen}
            inFocusedDialog={inFocusedDialog}
            actorRowBehaviorByUuid={actorRowBehaviorByUuid}
            beforeActorContentByUuid={beforeActorContentByUuid}
          />
        )}
        {extraActorContent}

        {!hideGeneralSection &&
          !showTimelineOnboarding &&
          selectedType === 'key_cooldown' && (
            <div className='relative z-10 group'>
              <ActorHeader title={<span>Everyone</span>} />
              <div className='flex items-start gap-1'>
                <ActorAbilityContainer
                  labels={
                    <>
                      {sortedGeneralRows.map((row) => {
                        if (row.spellId === null) {
                          const noteName =
                            generalAbilities.find(
                              (ability) => ability.id === NOTE_ABILITY_ID,
                            )?.name ?? 'Note'
                          return (
                            <div
                              key='general-note'
                              className='flex items-center gap-2'
                            >
                              <div className='w-40 max-w-40 shrink-0 h-5 text-xs text-foreground truncate flex items-center gap-2'>
                                <span className='truncate grow text-right'>
                                  {noteName}
                                </span>
                                <BlizzardIcon
                                  iconName={NOTE_ICON_NAME}
                                  size='small'
                                  className='w-5 h-5 rounded border border-border'
                                />
                              </div>
                            </div>
                          )
                        }
                        const abilityName =
                          generalAbilityById[row.spellId]?.name ??
                          `Spell ${row.spellId}`
                        return (
                          <div
                            key={row.spellId}
                            className='flex items-center gap-2'
                          >
                            <a
                              href={`https://wowhead.com/spell=${row.spellId}`}
                              target='_blank'
                              rel='noreferrer'
                              className='w-40 max-w-40 shrink-0 h-5 text-xs text-foreground truncate flex items-center gap-2'
                            >
                              <span className='truncate grow text-right'>
                                {abilityName}
                              </span>
                              <SpellIcon
                                spellId={row.spellId}
                                className='w-5 h-5 rounded border border-border'
                              />
                            </a>
                          </div>
                        )
                      })}
                    </>
                  }
                />
                <div
                  className='grow min-w-0'
                  onMouseMove={handleTimelineHoverMove}
                  onMouseLeave={handleTimelineHoverLeave}
                >
                  <ActorTimelineContainer
                    storageTarget={{ type: 'general' }}
                    rows={sortedGeneralRows}
                    hoverTime={visibleHoverTime}
                    onHoverTimeChange={handleTimelineHoverTimeChange}
                    onCastNotePopoverOpenChange={setGeneralCastPopoverOpen}
                    className={
                      inFocusedDialog ? 'text-card' : 'text-card-nested'
                    }
                  />
                </div>
              </div>
            </div>
          )}

        {visibleCursors.map((cursor) => (
          <div
            key={cursor.viewer.key}
            className='absolute z-40 pointer-events-none'
            style={{
              left: `${cursor.x * 100}%`,
              top: `${cursor.y * 100}%`,
              transition: 'left 120ms linear, top 120ms linear',
              willChange: 'transform',
            }}
          >
            <div
              className={`w-2 h-2 rounded-full ${getViewerColorClass(
                viewerIndexByKey[cursor.viewer.key] ?? 0,
              )}`}
            />
            <div className='text-[10px] px-1 rounded bg-card border border-border mt-0.5 whitespace-nowrap'>
              {cursor.viewer.label}
            </div>
          </div>
        ))}
      </div>
      <ImportFromWarcraftLogsDialog
        open={isImportDialogOpen}
        onOpenChange={setIsImportDialogOpen}
        encounterId={raidPlan.encounterId}
        planId={raidPlan.id}
        onImported={(updatedPlan) => {
          const importedTimeline = cloneTimeline(updatedPlan.timeline)
          updateTimeline((next) => {
            Object.keys(next).forEach((key) => {
              delete (next as Record<string, unknown>)[key]
            })
            Object.assign(next, importedTimeline)
          }, false)
        }}
      />
    </div>
  )
}

export default TeamPlanTimeline
